/**
 * Área "Matriz de Produtos (Serviços)": cliente por serviço, com o que ela já contratou, o que
 * finalizou e o que falta. Porte de `carregarMatrizServicos` (`admin.html:3631-3663`), com o CSS de
 * `admin.html:700-722`.
 *
 * Toda abertura relê a lista do zero, como o clique na aba do legado (`admin.html:6451`). A lista
 * é a mesma da área de Clientes e do quadro de Vendas (`matriz/carregar.ts`).
 *
 * O QUE A PEÇA `Tabela` DO CHASSI NÃO FAZ, e como foi resolvido aqui sem editar `packages/`:
 *   · dica no cabeçalho (o nome completo do serviço no `title` do `<th>`, `admin.html:3652`): o
 *     título de cada coluna vai como `<abbr title>`, que a `Tabela` põe dentro do `<th>` como veio;
 *   · largura fixa por coluna (`colgroup`, `admin.html:3651`): a tabela rola de lado no celular,
 *     como a do legado com `min-width: 560px`, e o nome é cortado com reticências.
 */

// Estado e efeito do React.
import { useEffect, useState } from 'react';
// A tabela e o ícone de visto da plataforma: o produto não desenha nenhum dos dois.
import { Tabela, icone, type Coluna } from '@complexity/ui';
// A fonte de dados do painel.
import { usePainel } from '../../contexto.js';
// Os nomes completos dos serviços, para a dica.
import { PRODUTOS_LEAD } from '../vendas/constantes.js';
// A lista de clientes.
import { carregarMapaDeClientes } from './carregar.js';
// A regra pura da matriz.
import {
  CHAVES_DA_MATRIZ,
  PRODUTOS_LEAD_ABREVIADO,
  linhasDaMatriz,
  nomeNaMatriz,
  situacaoDoServico,
  type LinhaDaMatriz,
  type SituacaoDoServico,
} from './regras.js';

// O visto do Chassi. `IconeVisto` existe em `Icones.tsx:62`, mas não sai pelo índice do pacote; o
// `check` sai, pelo mapa `ICONES` (`Icones.tsx:860`), e é o mesmo tique de uma linha.
const IconeDeVisto = icone('check');

// A dica e a aparência de cada situação (`admin.html:3656-3658`, `719-721`).
const CELULA: Readonly<Record<SituacaoDoServico, { readonly dica: string; readonly classe: string }>> = {
  // Finalizado: o visto em cinza (`var(--muted)`).
  finalizado: { dica: 'Finalizado', classe: 'text-texto-fraco' },
  // Em andamento: o visto forte (`var(--dark)`, peso 600).
  andamento: { dica: 'Em andamento', classe: 'text-texto' },
  // Lacuna: o traço bem claro (`var(--border)`).
  lacuna: { dica: 'Ainda não contratou', classe: 'text-texto-tenue' },
};

/**
 * O cabeçalho com a dica. `Coluna.titulo` é tipado como texto, e a `Tabela` só o desenha dentro
 * do `<th>` (`packages/ui/src/components/Table.tsx:97`); a conversão abaixo é a única forma de
 * entregar a dica sem editar o Chassi. Pedido registrado: `Coluna.dica` na peça.
 */
function tituloComDica(abreviado: string, completo: string): string {
  return (
    <abbr title={completo} className="no-underline">
      {abreviado}
    </abbr>
  ) as unknown as string;
}

// As colunas, montadas uma vez: Cliente, os oito serviços e Lacunas (`admin.html:3652`).
const COLUNAS: readonly Coluna<LinhaDaMatriz>[] = [
  {
    chave: 'cliente',
    titulo: 'Cliente',
    // O nome cortado com reticências, e inteiro na dica (`admin.html:3660`, `718`).
    celula: (linha) => (
      <span title={linha.cliente.nome_completo || linha.cliente.email || ''} className="block max-w-[12rem] truncate font-medium">
        {nomeNaMatriz(linha.cliente)}
      </span>
    ),
  },
  ...CHAVES_DA_MATRIZ.map<Coluna<LinhaDaMatriz>>((chave) => ({
    chave,
    titulo: tituloComDica(PRODUTOS_LEAD_ABREVIADO[chave] ?? chave, PRODUTOS_LEAD[chave] ?? chave),
    celula: (linha) => {
      const situacao = situacaoDoServico(linha.produtos, chave);
      const { dica, classe } = CELULA[situacao];
      return (
        // A dica fica no alvo, e o nome acessível é a própria dica: o visto é desenho.
        <span title={dica} role="img" aria-label={dica} data-situacao={situacao} className={`flex justify-center font-semibold ${classe}`}>
          {situacao === 'lacuna' ? '-' : IconeDeVisto ? <IconeDeVisto aria-hidden="true" className="h-cx-4 w-cx-4" /> : null}
        </span>
      );
    },
  })),
  {
    chave: 'lacunas',
    titulo: 'Lacunas',
    celula: (linha) => (
      <span data-lacunas className="block text-center font-semibold">
        {linha.lacunas}
      </span>
    ),
  },
];

export function TelaMatriz() {
  const { dados } = usePainel();
  // `null` enquanto a lista carrega.
  const [linhas, setLinhas] = useState<LinhaDaMatriz[] | null>(null);

  useEffect(() => {
    // Protege contra desenhar numa tela que já saiu.
    let vivo = true;
    void carregarMapaDeClientes(dados).then((lista) => {
      if (vivo) setLinhas(linhasDaMatriz(lista));
    });
    return () => {
      vivo = false;
    };
  }, [dados]);

  if (!linhas) {
    return (
      <p className="text-cx-sm text-texto-suave" role="status">
        Carregando...
      </p>
    );
  }

  // Sem cliente, a frase do legado (`admin.html:3635-3637`); a `Tabela` a desenha no lugar da tabela.
  return (
    <Tabela
      rotulo="Matriz de Produtos (Serviços)"
      colunas={COLUNAS}
      linhas={linhas}
      chaveDaLinha={(linha) => linha.cliente.user_id}
      vazio="Nenhuma cliente cadastrada ainda."
    />
  );
}
